import {
  ActivityDetails,
  ActivityUpdate,
  CreateDocumentRequest,
  CreateLinkRequest,
  CreateProjectFunctionRequest,
  CreateProjectRequest,
  isUserPhaseResponsibleOrAbove,
  isUserProjectEditorOrAbove,
  isUserProjectMember,
  isUserProjectResponsible,
  MergeFunctionRequest,
  PhaseCreate,
  Project,
  ProjectMapTemplate,
  ProjectMapTemplateLink,
  ProjectShiftElementsRequest,
  UpdateLinkRequest,
  UpdateProjectDetailsRequest,
  UpdateProjectRequest,
  UpdateProjFunctionRequest,
  UserSummary,
  validateAddDocumentRequest,
  validateAddFunctionRequest,
  validateAddLinkRequest,
  validateAddWorkstreamRequest,
  validateCreateProjectRequest,
  validateInsertPhaseBeforeRequest,
  validateMergeFunctionRequest,
  validateProjectShiftElementsRequest,
  validateRemoveActionRequest,
  validateRemoveCCRequest,
  validateRemoveDocumentRequest,
  validateRemoveFunctionRequest,
  validateRemoveLinkRequest,
  validateRemoveMemberRequest,
  validateRemovePhaseRequest,
  validateRemoveWorkstreamRequest,
  validateUpdateActivityRequest,
  validateUpdateDocumentRequest,
  validateUpdateFunctionRequest,
  validateUpdateLinkRequest,
  validateUpdateProjectDetailsRequest,
  validateUpdateProjectRequest,
  validateUpdateWorkstreamRequest,
  WorkstreamCreate,
  WorkstreamUpdate,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { useProfileStore } from './profileStore';
import { useProjectStore } from './projectStore';
import { useTemplateStore } from './templateStore';
import { useUserStore } from './userStore';

export const useProjectRulesStore = defineStore('projectRulesStore', {
  getters: {
    user() {
      return useProfileStore().profile;
    },

    project() {
      return (projectId: string): Project => {
        const project = useProjectStore().project(projectId);

        return project;
      };
    },

    activity() {
      return (projectId: string, activityId: string) => {
        return useProjectStore().activitySummary(projectId, activityId);
      };
    },

    criterion() {
      return (projectId: string, activityId: string, criterionId: string) => {
        return useProjectStore().criterion(projectId, activityId, criterionId);
      };
    },

    action() {
      return (projectId: string, activityId: string, actionId: string) => {
        return useProjectStore().action(projectId, activityId, actionId);
      };
    },

    hasProject() {
      return (projectId: string) => {
        return useProjectStore().hasProject(projectId);
      };
    },

    hasMembers() {
      return (projectId: string) => {
        return useProjectStore().hasMembers(projectId);
      };
    },

    projectMembers() {
      return (projectId: string) => {
        const project = useProjectStore().projectMembers(projectId);

        return project;
      };
    },
  },

  actions: {
    isProjectResponsible(projectId: string) {
      if (!this.hasProject(projectId)) {
        return false;
      }

      return isUserProjectResponsible(
        this.user.id,
        this.project(projectId).responsible,
      );
    },

    isIdProjectResponsible(userId: string, projectId: string) {
      return isUserProjectResponsible(
        userId,
        this.project(projectId).responsible,
      );
    },

    isUserProjectMember(projectId: string) {
      if (!this.hasMembers(projectId)) {
        return false;
      }

      return isUserProjectMember(this.user.id, this.projectMembers(projectId));
    },

    isProjectEditorOrAbove(projectId: string) {
      if (!this.hasProject(projectId)) {
        return false;
      }
      const project = this.project(projectId);

      return isUserProjectEditorOrAbove(
        this.user.id,
        project.responsible,
        project.editor_ids,
      );
    },

    isPhaseResponsibleOrAbove(projectId: string, phaseId: string) {
      const project = this.project(projectId);

      return isUserPhaseResponsibleOrAbove(
        this.user.id,
        project,
        phaseId,
        project.editor_ids,
      );
    },

    validateCreateProjectRequest(payload: CreateProjectRequest) {
      const template = ref<ProjectMapTemplate>();
      const projectOwnerScopes =
        useUserStore().userDetails(payload.responsible).scopes.project_reader ||
        [];
      if (payload.template) {
        template.value = useTemplateStore().template(payload.template);
      }

      return validateCreateProjectRequest(
        payload,
        this.user.scopes.project_creator,
        this.user.roles,
        projectOwnerScopes,
        template.value?.scopes,
        template.value?.phases.length,
      );
    },

    validateUpdateProjectDetailsRequest(
      projectId: string,
      payload: UpdateProjectDetailsRequest,
    ) {
      const project = this.project(projectId);
      const template = ref<ProjectMapTemplateLink>();
      if (
        project.template &&
        useTemplateStore().hasTemplateSummary(project.template)
      ) {
        template.value = useTemplateStore().templateLink(project.template);
      }

      return validateUpdateProjectDetailsRequest(
        project,
        payload,
        this.projectMembers(projectId),
        this.user.scopes.project_reader,
        template.value?.scopes,
        this.user.roles,
      );
    },

    validateUpdateProjectRequest(
      projectId: string,
      payload: UpdateProjectRequest,
    ) {
      return validateUpdateProjectRequest(this.project(projectId), payload);
    },

    validateInsertPhaseBeforeRequest(
      projectId: string,
      before: string | undefined,
      createPhaseRequest: PhaseCreate,
    ) {
      return validateInsertPhaseBeforeRequest(
        this.project(projectId),
        before,
        createPhaseRequest,
        this.projectMembers(projectId),
      );
    },

    validateRemovePhaseRequest(projectId: string, phaseId: string) {
      return validateRemovePhaseRequest(this.project(projectId), phaseId);
    },

    validateAddDocumentRequest(
      projectId: string,
      payload: CreateDocumentRequest,
    ) {
      return validateAddDocumentRequest(this.project(projectId), payload);
    },

    validateUpdateDocumentRequest(
      projectId: string,
      documentId: string,
      payload: CreateDocumentRequest,
    ) {
      return validateUpdateDocumentRequest(
        this.project(projectId),
        documentId,
        payload,
      );
    },

    validateRemoveDocumentRequest(projectId: string, documentId: string) {
      return validateRemoveDocumentRequest(this.project(projectId), documentId);
    },

    validateAddFunctionRequest(
      projectId: string,
      payload: CreateProjectFunctionRequest,
    ) {
      return validateAddFunctionRequest(
        this.project(projectId),
        payload,
        this.projectMembers(projectId),
      );
    },

    validateMergeFunctionRequest(
      projectId: string,
      targetFunction: MergeFunctionRequest,
      sourceFunctionId: string,
    ) {
      return validateMergeFunctionRequest(
        this.project(projectId),
        targetFunction,
        sourceFunctionId,
      );
    },

    validateUpdateFunctionRequest(
      projectId: string,
      functionUpdateRequest: UpdateProjFunctionRequest,
      functionId: string,
      members: UserSummary[],
    ) {
      return validateUpdateFunctionRequest(
        this.project(projectId),
        functionUpdateRequest,
        functionId,
        members,
      );
    },

    validateRemoveFunctionRequest(projectId: string, functionId: string) {
      return validateRemoveFunctionRequest(this.project(projectId), functionId);
    },

    validateAddWorkstreamRequest(
      projectId: string,
      payload: WorkstreamCreate,
      before: string | undefined,
    ) {
      return validateAddWorkstreamRequest(
        this.project(projectId),
        payload,
        before,
      );
    },

    validateUpdateWorkstreamRequest(
      projectId: string,
      payload: WorkstreamUpdate,
      workstreamId: string,
    ) {
      return validateUpdateWorkstreamRequest(
        this.project(projectId),
        payload,
        workstreamId,
      );
    },

    validateRemoveWorkstreamRequest(projectId: string, workstreamId: string) {
      return validateRemoveWorkstreamRequest(
        this.project(projectId),
        workstreamId,
      );
    },

    validateRemoveMemberRequest(
      projectId: string,
      members: UserSummary[],
      memberToRemove: string,
    ) {
      return validateRemoveMemberRequest(
        this.project(projectId).responsible,
        members,
        memberToRemove,
      );
    },

    validateAddLinkRequest(projectId: string, payload: CreateLinkRequest) {
      return validateAddLinkRequest(payload, this.project(projectId).links);
    },

    validateUpdateLinkRequest(
      projectId: string,
      payload: UpdateLinkRequest,
      linkIdToUpdate: string,
    ) {
      return validateUpdateLinkRequest(
        this.project(projectId).links,
        payload,
        linkIdToUpdate,
      );
    },

    validateRemoveLinkRequest(projectId: string, linkIdToRemove: string) {
      return validateRemoveLinkRequest(
        this.project(projectId).links,
        linkIdToRemove,
      );
    },

    validateUpdateActivityRequest(
      projectId: string,
      activityId: string,
      activityPayload: ActivityUpdate,
    ) {
      return validateUpdateActivityRequest(
        this.project(projectId),
        this.activity(projectId, activityId) as ActivityDetails,
        activityPayload,
      );
    },

    validateRemoveCCRequest(
      projectId: string,
      activityId: string,
      criterionId: string,
    ) {
      return validateRemoveCCRequest(
        this.activity(projectId, activityId),
        this.criterion(projectId, activityId, criterionId),
      );
    },

    validateRemoveActionRequest(
      projectId: string,
      activityId: string,
      actionId: string,
    ) {
      return validateRemoveActionRequest(
        this.activity(projectId, activityId),
        this.action(projectId, activityId, actionId),
      );
    },

    validateApplySpaceToolRequest(
      projectId: string,
      shiftElementsRequest: ProjectShiftElementsRequest,
    ) {
      return validateProjectShiftElementsRequest(
        this.project(projectId),
        shiftElementsRequest,
      );
    },
  },
});
